/*
 * Authors: mariushirjanu
 * Created: 11/04/2022
 */
import _ from 'underscore';
import { util } from '@app/shared/util';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ICompanyCountry } from '@type_definitions/datatypes';
import { DialogBusyButtonFn, IDialogButton } from '@app/shared/message/ga-message.service';
import { IPaymentMatchData, IPaymentMatchTransaction, PaymentMatcherDataService } from './payment-matcher-data.service';
import { IPaymentMatch, PaymentMatcherService } from './payment-matcher.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { PaymentListMatchesComponent } from './payment-list-matches.component';

const BUTTON_SAVE: string = 'save';
const BUTTON_CONFIRM: string = 'confirm';

@Component({
  selector: 'payment-matcher-dialog',
  template: `
    <default-dialog [title]="title" [buttons]="buttons">
      <div class="row">
        <div class="col-md-8 col-lg-9 col-xxl-10">
          <payment-list-matches [model]="model" [matchToAdd]="matchToAdd" (onRemoveFromMatches)="onRemoveFromMatches($event)" (onChange)="onPaymentMatchesChanged($event)"></payment-list-matches>
          <payment-list-invoices [transactionId]="model?.transactionId" [processingStatus]="model?.processingStatus" [invoiceToAdd]="invoiceToAdd" (onRemoveFromInvoices)="onRemoveFromInvoices($event)" (onIssuedToChange)="onIssuedToChange($event)"></payment-list-invoices>
        </div>
        <div class="col-md-4 col-lg-3 col-xxl-2">
          <payment-transaction-details [(model)]="model" [issuedTo]="issuedTo"></payment-transaction-details>
        </div>
      </div>
    </default-dialog>
  `
})
export class PaymentMatcherDialogComponent implements OnInit {
  @ViewChild(PaymentListMatchesComponent) public paymentListMatchesComponent:PaymentListMatchesComponent;

  @Input() public model:IPaymentMatchTransaction;
  @Input() public title:string;
  @Output() public onCancel:EventEmitter<void> = new EventEmitter();
  @Output() public onSaving:EventEmitter<boolean> = new EventEmitter();
  @Output() public onSaved:EventEmitter<any> = new EventEmitter();

  public buttons:IDialogButton[];
  public matchToAdd:IPaymentMatch;
  public invoiceToAdd:IPaymentMatch;
  private paymentMatches:IPaymentMatch[];
  public issuedTo:ICompanyCountry[];
  private statusNewOrProposed:boolean;

  constructor(
    private cdr:ChangeDetectorRef,
    private paymentMatcherService:PaymentMatcherService,
    private paymentMatcherDataService:PaymentMatcherDataService,
    private validateService:GaValidateService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.statusNewOrProposed = this.paymentMatcherService.isStatusNewOrProposed(this.model.processingStatus);

    this.buttons = [ {
      id: BUTTON_SAVE,
      label: 'Save & Close',
      className: 'btn btn-success',
      disabled: !this.statusNewOrProposed,
      onClick: (updateBusyButtonStatus:DialogBusyButtonFn) => {
        this.doSave(updateBusyButtonStatus);
      }
    }, {
      id: BUTTON_CONFIRM,
      label: 'Confirm Matches',
      className: 'btn btn-primary',
      disabled: !this.statusNewOrProposed,
      onClick: (updateBusyButtonStatus:DialogBusyButtonFn) => {
        this.doSave(updateBusyButtonStatus, true);
      }
    }, {
      label: 'GA.BUTTONS.CANCEL',
      className: 'btn btn-danger',
      onClick: () => this.onCancel.emit()
    } ];
  }

  public onRemoveFromMatches(invoice:IPaymentMatch):void {
    this.invoiceToAdd = _.clone(invoice);
  }

  public onRemoveFromInvoices(invoice:IPaymentMatch):void {
    this.matchToAdd = _.clone(invoice);
  }

  public onPaymentMatchesChanged(paymentMatches:IPaymentMatch[]):void {
    this.paymentMatches = paymentMatches;

    // when there are no invoices -> disable confirm button
    const button:IDialogButton = _.find(this.buttons, { id: BUTTON_CONFIRM });
    button.disabled = !this.paymentMatches?.length ? true : !this.statusNewOrProposed;
    this.cdr.detectChanges();
  }

  public onIssuedToChange(issuedTo:ICompanyCountry[]):void {
    this.issuedTo = issuedTo;
    this.cdr.detectChanges();
  }

  public doSave(updateBusyButtonStatus:DialogBusyButtonFn, confirm?:boolean):void {
    this.validateService.validateSubcomponents([ this.paymentListMatchesComponent.validate() ])
      .then(() => {
        updateBusyButtonStatus(true);
        this.onSaving.emit(true);

        Promise.all([
          this.paymentMatcherDataService.saveMatches(this.model.transactionId, this.paymentMatcherService.createPaymentMatchesRequest(this.paymentMatches), confirm),
          this.paymentMatcherDataService.saveComment(this.model.transactionId, this.model?.comments)
        ])
          .then(util.spread((savedPaymentMatches:IPaymentMatchData[]) => {
            this.onSaved.emit({ responseData: savedPaymentMatches });
          }))
          .finally(() => {
            updateBusyButtonStatus(false);
            this.onSaving.emit(false);
          });
      });
  }
}